import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getProducts,
  addProduct as addProductService,
  updateProduct as updateProductService,
  deleteProduct as deleteProductService,
  updateProductStatus as updateProductStatusService,
  deleteProductImages as deleteProductImagesService,
  reorderProductImages as reorderProductImagesService,
} from "../../services/productService";
import { toast } from "react-toastify";

const initialState = {
  products: [],
  status: "idle",
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

// Fetch all products with filters
export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getProducts(params);
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch products";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Add a new product
export const addProduct = createAsyncThunk(
  "products/addProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const response = await addProductService(productData);
      toast.success(response.message || "Product added successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to add product";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Update a product
export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async ({ id, productData }, { rejectWithValue }) => {
    try {
      const response = await updateProductService(id, productData);
      toast.success(response.message || "Product updated successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to update product";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Update a product status
export const updateProductStatus = createAsyncThunk(
  "products/updateProductStatus",
  async ({ id, statusObj }, { rejectWithValue }) => {
    try {
      const response = await updateProductStatusService(id, statusObj);
      toast.success(response.message || "Status updated successfully!");
      return { id, statusObj, updatedData: response.data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to update status";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Delete one or more already-saved product images
export const deleteProductImages = createAsyncThunk(
  "products/deleteProductImages",
  async ({ id, imagePaths }, { rejectWithValue }) => {
    try {
      const response = await deleteProductImagesService(id, imagePaths);
      toast.success(response.message || "Image(s) deleted successfully!");
      return { id, updatedData: response.data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete image(s)";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Reorder already-saved product images
export const reorderProductImages = createAsyncThunk(
  "products/reorderProductImages",
  async ({ id, images }, { rejectWithValue }) => {
    try {
      const response = await reorderProductImagesService(id, images);
      toast.success(response.message || "Images reordered successfully!");
      return { id, updatedData: response.data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to reorder images";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Delete a product
export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteProductService(id);
      toast.success(response.message || "Product deleted successfully!");
      return id;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete product";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Helper: pulls an updated `img` array out of a thunk response payload,
// regardless of whether the backend returned the full product, just the
// image array, or an object containing `img`.
const extractUpdatedImg = (updatedData) => {
  if (!updatedData) return null;
  if (Array.isArray(updatedData)) return updatedData;
  if (Array.isArray(updatedData.img)) return updatedData.img;
  return null;
};

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetProductState: (state) => {
      state.products = [];
      state.status = "idle";
      state.error = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      };
    },
    setProducts: (state, action) => {
      state.products = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.products = action.payload.data || [];

        // Store backend pagination
        state.pagination = action.payload.pagination || {
          total: state.products.length,
          page: 1,
          limit: state.products.length,
          totalPages: 1,
        };

        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.products = [];
        state.pagination = {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        };
      })

      // Add Product
      .addCase(addProduct.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.data) {
          state.products.unshift(action.payload.data);
          state.pagination.total += 1;
        }
        state.error = null;
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updatedProduct = action.payload.data;

        if (updatedProduct && Array.isArray(state.products)) {
          const index = state.products.findIndex(
            (product) => product._id === updatedProduct._id,
          );
          if (index !== -1) {
            state.products[index] = updatedProduct;
          }
        }
        state.error = null;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Product Status
      .addCase(updateProductStatus.pending, (state) => {
        // Don't change overall status for status updates
      })
      .addCase(updateProductStatus.fulfilled, (state, action) => {
        const { id, statusObj, updatedData } = action.payload;

        if (Array.isArray(state.products)) {
          const index = state.products.findIndex(
            (product) => product._id === id,
          );
          if (index !== -1) {
            if (updatedData) {
              state.products[index] = updatedData;
            } else {
              state.products[index] = {
                ...state.products[index],
                ...statusObj,
              };
            }
          }
        }
      })
      .addCase(updateProductStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete Saved Product Images
      .addCase(deleteProductImages.fulfilled, (state, action) => {
        const { id, updatedData } = action.payload;
        if (Array.isArray(state.products)) {
          const index = state.products.findIndex(
            (product) => product._id === id,
          );
          if (index !== -1 && updatedData) {
            const newImg = extractUpdatedImg(updatedData);
            if (newImg) {
              state.products[index] = { ...state.products[index], img: newImg };
            } else if (updatedData._id) {
              state.products[index] = updatedData;
            }
          }
        }
      })
      .addCase(deleteProductImages.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Reorder Saved Product Images
      .addCase(reorderProductImages.fulfilled, (state, action) => {
        const { id, updatedData } = action.payload;
        if (Array.isArray(state.products)) {
          const index = state.products.findIndex(
            (product) => product._id === id,
          );
          if (index !== -1 && updatedData) {
            const newImg = extractUpdatedImg(updatedData);
            if (newImg) {
              state.products[index] = { ...state.products[index], img: newImg };
            } else if (updatedData._id) {
              state.products[index] = updatedData;
            }
          }
        }
      })
      .addCase(reorderProductImages.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.payload;
        state.products = state.products.filter(
          (product) => product._id !== deletedId,
        );
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        state.error = null;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError, resetProductState, setProducts } =
  productSlice.actions;

// Selectors
export const selectAllProducts = (state) => state.products.products;
export const selectProductById = (state, productId) =>
  state.products.products.find((product) => product._id === productId);
export const selectProductStatus = (state) => state.products.status;
export const selectProductError = (state) => state.products.error;
export const selectProductPagination = (state) => state.products.pagination;
export const selectActiveProducts = (state) =>
  state.products.products.filter((product) => product.status === "Active");

export default productSlice.reducer;
